import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AdminNavigation from "./AdminNavigation";
import UpdateUserModalProfile from "./UpdateUserModalProfile";
import ViewUserModalProfile from "./ViewUserModalProfile";

function LadiesProfileAdmin() {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [statusMessage, setStatusMessage] = useState("");
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const token = localStorage.getItem("adminToken");
    const API_URL = process.env.REACT_APP_API_URL;

    useEffect(() => {
        const userRole = localStorage.getItem("userRole");
        if (!userRole || userRole !== "1") {
            navigate("/login");
        }
    }, [navigate]);

    const fetchUsersData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.post(
                `${API_URL}/users/getfiltereduser/`,
                { gender: "female" },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            setUsers(response.data.data || []);
        } catch (error) {
            console.error("Error fetching female users:", error);
            if (error.response?.status === 401) {
                toast.error("Unauthorized. Please log in again.");
                navigate("/login");
            } else {
                toast.error(
                    "Failed to load female users: " +
                    (error.response?.data?.message || error.message || "Unknown error")
                );
            }
        } finally {
            setLoading(false);
        }
    }, [token, navigate]);

    useEffect(() => {
        if (token) {
            fetchUsersData();
        } else {
            navigate("/login");
        }
    }, [fetchUsersData, token, navigate]);

    const deleteUser = async (id) => {
        if (!window.confirm("Are you sure you want to delete this user?")) return;
        setActionLoading(true);
        try {
            const response = await axios.delete(`${API_URL}/users/delete/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.status === 200) {
                toast.success("User deleted successfully!");
                fetchUsersData();
            } else {
                throw new Error("Unexpected response status: " + response.status);
            }
        } catch (error) {
            console.error("Error deleting user:", error);
            toast.error(
                "Failed to delete user: " +
                (error.response?.data?.message || error.message || "Unknown error")
            );
            if (error.response?.status === 401) navigate("/login");
        } finally {
            setActionLoading(false);
        }
    };

    const handleEditUser = (user) => {
        setSelectedUserId(user.id);
        setShowUpdateModal(true);
    };

    const handleViewUser = (user) => {
        setSelectedUserId(user.id);
        setShowViewModal(true);
    };

    const handleCloseUpdateModal = () => {
        setShowUpdateModal(false);
        setSelectedUserId(null);
        fetchUsersData();
    };

    const handleCloseViewModal = () => {
        setShowViewModal(false);
        setSelectedUserId(null);
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    if (loading) {
        return (
            <div className="loading-overlay text-center">
                <img src="/img/loader.gif" alt="Loading..." />
            </div>
        );
    }

    return (
        <div id="wrapper" className="toggled">
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
            <AdminNavigation />
            <div id="page-content-wrapper">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12">
                            {statusMessage && (
                                <div className="alert alert-success mt-3" role="alert">
                                    {statusMessage}
                                </div>
                            )}
                            <Link to={"/admin/add_users"} className="btn btn-primary">Add Female User</Link>
                            <h5>All Female Users</h5>

                            <div className="table-responsive">
                                <table className="table table-light table-striped">
                                    <thead>
                                        <tr>
                                            <th>Sno</th>
                                            <th>First Name</th>
                                            <th>Last Name</th>
                                            <th>Email</th>
                                            <th>Gender</th>
                                            <th>DOB</th>
                                            <th>Username</th>
                                            <th>Created At</th>
                                            <th>Updated At</th>
                                            <th>Profile</th>
                                            <th colSpan={2}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.length === 0 ? (
                                            <tr>
                                                <td colSpan="11" className="text-center">
                                                    No female users found.
                                                </td>
                                            </tr>
                                        ) : (
                                            users.map((user, index) => (
                                                <tr key={user.id}>
                                                    <td>{index + 1}</td>
                                                    <td>{user.fname || "N/A"}</td>
                                                    <td>{user.lname || "N/A"}</td>
                                                    <td>{user.email || "N/A"}</td>
                                                    <td>{user.gender || "N/A"}</td>
                                                    <td>{formatDate(user.dob)}</td>
                                                    <td>{user.username || "N/A"}</td>
                                                    <td>{formatDate(user.createdAt)}</td>
                                                    <td>{formatDate(user.updatedAt)}</td>
                                                    <td>
                                                        <button
                                                            onClick={() => handleViewUser(user)}
                                                            className="btn btn-success"
                                                            disabled={actionLoading}
                                                        >
                                                            View
                                                        </button>
                                                    </td>
                                                    <td>
                                                        <button
                                                            onClick={() => handleEditUser(user)}
                                                            className="btn btn-primary"
                                                            disabled={actionLoading}
                                                        >
                                                            Edit
                                                        </button>
                                                    </td>
                                                    <td>
                                                        <button
                                                            onClick={() => deleteUser(user.id)}
                                                            className="btn btn-danger"
                                                            disabled={actionLoading}
                                                        >
                                                            Delete
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <UpdateUserModalProfile
                show={showUpdateModal}
                handleClose={handleCloseUpdateModal}
                userId={selectedUserId}
                token={token}
                fetchUsersData={fetchUsersData}
            />
            <ViewUserModalProfile
                show={showViewModal}
                handleClose={handleCloseViewModal}
                userId={selectedUserId}
                token={token}
            />
        </div>
    );
}

export default LadiesProfileAdmin;